import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import BillPay from './quickActions/BillPay';
import MobileDeposit from './quickActions/MobileDeposit';
import Statements from './quickActions/Statements';
import CardServices from './quickActions/CardServices';

const QUICK_ACTIONS = [
  { id: 'billpay', label: 'Bills', icon: '💰', color: 'from-primary-500 to-primary-600' },
  { id: 'deposit', label: 'Cheque deposit', icon: '📱', color: 'from-secondary-500 to-secondary-600' },
  { id: 'cards', label: 'Cards', icon: '💳', color: 'from-accent-500 to-accent-600' },
  { id: 'statements', label: 'Statements', icon: '📄', color: 'from-primary-500 to-primary-600' },
  { id: 'paycontact', label: 'Pay a contact', icon: '⚡', color: 'from-secondary-500 to-secondary-600' },
  { id: 'rewards', label: 'Cashback', icon: '⭐', color: 'from-accent-500 to-accent-600' }
];

const VALID_IDS = new Set(QUICK_ACTIONS.map((a) => a.id));

export default function QuickActions({ accounts }) {
  const { currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const [activeAction, setActiveAction] = useState(null);

  // Open a specific quick action from nav hash (e.g. #quick-actions-cards, #quick-actions-billpay)
  useEffect(() => {
    const hash = (location.hash || '').replace(/^#/, '');
    if (hash.startsWith('quick-actions-')) {
      const id = hash.replace('quick-actions-', '');
      if (VALID_IDS.has(id)) {
        setActiveAction(id);
        sectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      }
    } else {
      setActiveAction(null);
    }
  }, [location.hash]);

  const handleSetActiveAction = (id) => {
    setActiveAction(id);
    navigate('/dashboard#quick-actions-' + id, { replace: true });
  };

  const handleBack = () => {
    setActiveAction(null);
    navigate('/dashboard', { replace: true });
  };

  return (
    <div ref={sectionRef} className="bg-white rounded-2xl shadow-card overflow-hidden">
      {!activeAction ? (
        <div className="space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 p-6">
            {QUICK_ACTIONS.map((action) => (
              <button
                key={action.id}
                onClick={() => handleSetActiveAction(action.id)}
                data-action-id={action.id}
                className="group relative overflow-hidden rounded-xl transition-all duration-300 hover:shadow-lg"
              >
                {/* Background with gradient */}
                <div className={`absolute inset-0 bg-gradient-card ${action.color} opacity-90 
                  group-hover:opacity-100 transition-opacity duration-300`}
                />
                
                {/* Content */}
                <div className="relative p-4 flex flex-col items-center">
                  <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-3 
                    group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                    <span className="text-2xl">{action.icon}</span>
                  </div>
                  <span className="text-sm font-medium text-white group-hover:scale-105 transition-transform duration-300">
                    {action.label}
                  </span>
                </div>

                {/* Decorative circle */}
                <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full transform translate-x-8 -translate-y-8" />
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="p-6 animate-fadeIn">
          <div className="mb-6">
            <button
              onClick={handleBack}
              className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Quick Actions
            </button>
          </div>

          <div className="bg-gray-50/50 rounded-xl p-6">
            {activeAction === 'billpay' && (
              <BillPay accounts={accounts} />
            )}
            {activeAction === 'deposit' && (
              <MobileDeposit 
                accounts={accounts}
                currentUser={currentUser}
              />
            )}
            {activeAction === 'statements' && (
              <Statements accounts={accounts} userEmail={currentUser?.email} />
            )}
            {activeAction === 'cards' && (
              <CardServices />
            )}
            {activeAction === 'paycontact' && (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-3xl">⚡</span>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Pay a contact – coming soon</h3>
                <p className="text-gray-600">We're adding instant payments so you can pay friends and family by mobile number. Check back soon.</p>
              </div>
            )}
            {activeAction === 'rewards' && (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
                  <span className="text-3xl">⭐</span>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Cashback</h3>
                <p className="text-gray-600">Our cashback programme is being updated. Check back soon.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
} 